import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';

import {
  AllowanceProposalProps,
  BenefitProposalProps,
  BillableHoursProposalProps,
  BonusProposalProps,
  ProposalPreviewProps,
  ProposalProps,
  ProposalStatus,
  ProposalType,
  SalaryProposalProps,
} from '~/data/proposals';
import { useGetProposals } from '~/data/proposals/useGetProposals';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { useHasChanged } from '~/hooks/useHasChanged';
import { UseVisibleOutput, useVisible } from '~/hooks/useVisible';
import { WorkflowsEmployeeProposalsDialog_OnUpdateLegacy } from '~/library/WorkflowsEmployeeProposalsDialog/component';
import {
  EmployeeProposalUpdateCallback,
  EmployeeProposalUpdateCallbackProps,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { useNewEmployeeProposal } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/useNewEmployeeProposal';

type PropsType = WorkflowsEmployeeProposalsDialog_OnUpdateLegacy & {
  proposalType: ProposalType;
};

type UseEmployeeProposalsOutput = {
  targetId: number;
  proposalPreview?: ProposalPreviewProps;
  newProposal?: ProposalProps;
  user: {
    isInitiator: boolean;
  };
  proposalsList: UseVisibleOutput & {
    proposals: ProposalProps[];
    hasProposals: boolean;
  };
  handleProposalUpdate: EmployeeProposalUpdateCallback;
  isLoading: boolean;
  createNew: {
    enabled: boolean;
    proposal: {};
  };
};

export const useEmployeeProposals = (
  props: PropsType,
): UseEmployeeProposalsOutput => {
  const { proposalType, onUpdateLegacy } = props;
  const { selectedEmployeeId } = useSelectedEmployeeId();

  const {
    user,
    proposalPreview,
    newProposal,
    isLoading: isLoadingNewEmployeeProposal,
  } = useNewEmployeeProposal({
    employeeId: selectedEmployeeId,
    proposalType,
  });

  const {
    data: { proposals: unsortedProposals },
    isLoading: isLoadingProposals,
    mutate,
  } = useGetProposals({
    targetId: selectedEmployeeId,
    proposalType,
    statuses: [
      ProposalStatus.PROPOSED,
      ProposalStatus.IN_PROGRESS,
      ProposalStatus.COMPLETED,
    ],
  });
  const isLoadingChanged = useHasChanged(isLoadingProposals);
  const hasProposalTypeChanged = useHasChanged(proposalType);

  // TODO Apply access check once the implementation is complete,
  //  this code segment should be commented until then
  // const { hasAccess } = useUserAccessToProposals({
  //   newProposal,
  //   proposals: unsortedProposals,
  // });

  const hasProposals = useMemo(
    () => unsortedProposals.length > 0,
    [unsortedProposals],
  );
  const proposalsListVisibility = useVisible(
    !isLoadingProposals && !hasProposals,
  );
  const { show, hide } = proposalsListVisibility;

  const proposals = useMemo(() => {
    const sortByProposalDate =
      <T extends ProposalProps>(
        dateResolver: (
          proposal: T,
        ) => string | number | Date | null | undefined,
      ) =>
      (a: T, b: T) => {
        return moment(dateResolver(a)).diff(moment(dateResolver(b)));
      };

    switch (proposalType) {
      case ProposalType.ALLOWANCE_PROPOSAL:
        return (unsortedProposals as AllowanceProposalProps[]).sort(
          sortByProposalDate((p) => p.proposalData?.startDate),
        );
      case ProposalType.BENEFIT_PROPOSAL:
        return (unsortedProposals as BenefitProposalProps[]).sort(
          sortByProposalDate((p) => p.proposalData?.startDate),
        );
      case ProposalType.BILLABLE_HOURS:
        return (unsortedProposals as BillableHoursProposalProps[]).sort(
          sortByProposalDate((p) => p.proposalData?.startDate),
        );
      case ProposalType.BONUS_PROPOSAL:
        return (unsortedProposals as BonusProposalProps[]).sort(
          sortByProposalDate((p) => p.proposalData?.paymentPeriodStartDate),
        );
      case ProposalType.SALARY_PROPOSAL:
        return (unsortedProposals as SalaryProposalProps[]).sort(
          sortByProposalDate((p) => p.proposalData?.startDate),
        );
      default:
        return unsortedProposals;
    }
  }, [unsortedProposals, proposalType]);

  useEffect(() => {
    if ((hasProposalTypeChanged || isLoadingChanged) && !isLoadingProposals) {
      if (hasProposals) {
        hide();
      } else {
        show();
      }
    }
  }, [
    hasProposalTypeChanged,
    isLoadingChanged,
    hasProposals,
    isLoadingProposals,
    hide,
    show,
  ]);

  const handleProposalUpdate = useCallback(
    (updateProps?: EmployeeProposalUpdateCallbackProps) => {
      mutate();
      if (!!onUpdateLegacy) {
        onUpdateLegacy([proposalType]);
      }
      if (updateProps?.isNewCreated) {
        hide();
      }
    },
    [mutate, hide, onUpdateLegacy, proposalType],
  );

  return {
    targetId: selectedEmployeeId,
    proposalPreview,
    newProposal,
    user,
    proposalsList: {
      proposals,
      hasProposals,
      ...proposalsListVisibility,
    },
    handleProposalUpdate,
    isLoading: isLoadingNewEmployeeProposal || isLoadingProposals,
    createNew: {
      enabled: true,
      proposal: {},
    },
  };
};
